// Achievements (docs/05 §9). Each check reads the state after a command, the state before it, the
// command and its events. Progress counters live in `meta.lifetime.counters` and run flags; they are
// updated by `sim/achievements.ts` before the checks run. Text lives in i18n as `ach.<id>.name/desc`.
import { B } from './balance';
import { CHARM_LIST, COLLECTOR_LIST, RELIC_LIST, SYMBOL_LIST } from './registry';
import type { Command, GameEvent, GameState, WinTier } from '../sim/types';

export interface AchievementCtx {
  state: GameState;
  prev: GameState;
  cmd: Command;
  events: GameEvent[];
}

export interface AchievementDef {
  id: string;
  /** Hidden in the panel until earned. */
  secret?: boolean;
  check(c: AchievementCtx): boolean;
}

const has = <K extends GameEvent['e']>(c: AchievementCtx, e: K, pred: (x: Extract<GameEvent, { e: K }>) => boolean = () => true) =>
  c.events.some((x) => x.e === e && pred(x as Extract<GameEvent, { e: K }>));
const tier = (c: AchievementCtx, t: WinTier) => has(c, 'SPIN_RESOLVED', (e) => e.snapshot.tier === t || (t === 'gorge' && e.snapshot.hits.some((h) => h.pattern === 'gorge')));
const paid = (c: AchievementCtx, n: number) => c.state.meta.lifetime.maxTribute >= n;
const count = (c: AchievementCtx, key: string) => c.state.meta.lifetime.counters[key] ?? 0;
const bonusDone = <M extends string>(c: AchievementCtx, mode: M) =>
  c.events.flatMap((e) => (e.e === 'BONUS_UPDATE' && e.bonus.mode === mode ? [e.bonus] : []));

/** Symbols the Almanac can list: everything you can draft (rotten fruit is found by rotting). */
export const ALMANAC_SYMBOLS = SYMBOL_LIST.filter((d) => !d.noDraft || d.rottenFrom).map((d) => d.id);

/** The eight Collectors of the Reckonings (the Last Supper is the final one, not a Collector). */
const COLLECTORS_8 = COLLECTOR_LIST.filter((c) => !c.final).map((c) => c.id);

export const ACHIEVEMENT_LIST: AchievementDef[] = [
  { id: 'A01', check: (c) => c.state.meta.lifetime.consumed >= 1 },
  { id: 'A02', check: (c) => paid(c, 5) },
  { id: 'A03', check: (c) => paid(c, 10) },
  { id: 'A04', check: (c) => paid(c, 20) },
  { id: 'A05', check: (c) => paid(c, 30) },
  { id: 'A06', check: (c) => (c.state.meta.nodes.G_GROW_1 ?? 0) > 0 },
  { id: 'A07', check: (c) => c.state.run?.size === 'cathedral' },
  { id: 'A08', check: (c) => c.state.meta.molts >= 1 },
  { id: 'A09', check: (c) => c.state.meta.form === 'gilded' },
  { id: 'A10', check: (c) => has(c, 'SPIN_RESOLVED', (e) => ['big', 'mega', 'epic', 'legendary', 'gorge'].includes(e.snapshot.tier)) },
  { id: 'A11', check: (c) => tier(c, 'legendary') },
  { id: 'A12', check: (c) => tier(c, 'gorge') },
  { id: 'A13', check: (c) => has(c, 'SPIN_RESOLVED', (e) => e.snapshot.hits.some((h) => h.pattern === 'bandit')) },
  { id: 'A14', check: (c) => count(c, 'honestPerfect') >= B.ACH_IRON_WRIST },
  { id: 'A15', check: (c) => count(c, 'nudgeWins') >= B.ACH_SURGEON },
  {
    id: 'A16',
    check: (c) =>
      has(c, 'SPIN_RESOLVED', (e) => e.snapshot.payout.gt(0) && e.snapshot.held.length > 1 && e.snapshot.held.filter(Boolean).length === e.snapshot.held.length - 1),
  },
  { id: 'A17', check: (c) => has(c, 'TRIBUTE_PAID') && (c.prev.run?.hunger ?? 0) >= B.HUNGER_MAX },
  { id: 'A18', check: (c) => (c.state.run?.flags.adoringStreak ?? 0) >= B.ACH_WELL_FED },
  { id: 'A19', check: (c) => has(c, 'CLEANSE') },
  { id: 'A20', check: (c) => has(c, 'TRIBUTE_PAID') && !!c.state.run?.flags.threeHexes },
  { id: 'A21', check: (c) => (c.state.run?.doom ?? 0) >= 6 },
  { id: 'A22', check: (c) => has(c, 'DEVOUR', (e) => e.kind === 'miracle') },
  {
    id: 'A23',
    check: (c) =>
      bonusDone(c, 'scratch').some((b) => {
        if (b.mode !== 'scratch' || !b.done || b.voided || b.payout.lte(0)) return false;
        const trap = b.cells.findIndex((x) => x.prize === 'eye');
        if (trap < 0) return false;
        const tx = trap % b.size;
        const ty = Math.floor(trap / b.size);
        const near = b.cells.filter((_, i) => i !== trap && Math.abs((i % b.size) - tx) <= 1 && Math.abs(Math.floor(i / b.size) - ty) <= 1);
        return near.length > 0 && near.reduce((a, x) => a + x.revealed, 0) / near.length >= B.ACH_SCRATCH_MASTER;
      }),
  },
  { id: 'A24', check: (c) => bonusDone(c, 'hilo').some((b) => b.mode === 'hilo' && b.done && !b.devoured && b.rung >= B.HILO_LADDER.length) },
  { id: 'A25', check: (c) => bonusDone(c, 'wheel').some((b) => b.mode === 'wheel' && b.ring >= 2) },
  { id: 'A26', check: (c) => !!c.state.meta.flags.plinkoPrince },
  { id: 'A27', check: (c) => !!c.state.meta.flags.theFeast },
  { id: 'A28', check: (c) => c.state.meta.lifetime.collectorsBeaten.length >= COLLECTORS_8.length && COLLECTORS_8.every((id) => c.state.meta.lifetime.collectorsBeaten.includes(id)) },
  { id: 'A29', check: (c) => c.state.meta.lifetime.dealsAccepted >= B.COVENANT_DEALS },
  { id: 'A30', check: (c) => c.state.meta.lifetime.dealsRefused >= B.PHONE_DEALS },
  { id: 'A31', check: (c) => c.state.meta.lifetime.ledgerPages.length >= 13 },
  { id: 'A32', check: (c) => c.state.meta.almanac.symbols.length >= ALMANAC_SYMBOLS.length && ALMANAC_SYMBOLS.every((id) => c.state.meta.almanac.symbols.includes(id)) },
  { id: 'A33', check: (c) => c.state.meta.almanac.charms.length >= CHARM_LIST.length && CHARM_LIST.every((d) => c.state.meta.almanac.charms.includes(d.id)) },
  { id: 'A34', check: (c) => c.state.meta.relics.owned.length >= RELIC_LIST.length && RELIC_LIST.every((d) => c.state.meta.relics.owned.includes(d.id)) },
  { id: 'A35', check: (c) => c.state.meta.lifetime.endings.includes('sated') },
  { id: 'A36', check: (c) => c.state.meta.lifetime.endings.includes('maw') },
  { id: 'A37', check: (c) => c.state.meta.lifetime.endings.includes('unplugged') },
  { id: 'A38', check: (c) => c.state.meta.lifetime.bestEndless >= B.ACH_INSATIABLE || (!!c.state.run?.flags.endless && (c.state.run?.window.n ?? 0) >= B.ACH_INSATIABLE) },
  { id: 'A39', check: (c) => has(c, 'TRIBUTE_PAID', (e) => e.n >= 20) && c.state.run?.size === 'bandit' },
  { id: 'A40', secret: true, check: (c) => !!c.state.meta.flags.hungerStrike },
];
